import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEOHead from "../components/SEOHead";
import logo from "../assets/logo.png";
import BackToTop from "../components/BackToTop";
import Reveal from "../components/Reveal";
import SpiderChart from "../components/SpiderChart";

const LandingPage = () => {
  const [scrolled, setScrolled] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState(0);

  const categories = [
    { name: "Strategy", fullName: "Positioning, differentiation, and market approach", questions: 18 },
    { name: "Marketing", fullName: "Demand generation, campaigns, and funnel design", questions: 21 },
    { name: "Sales", fullName: "Conversion process, pipeline control, and buyer journey", questions: 20 },
    { name: "CRM", fullName: "CRM structure, reporting discipline, and lead management", questions: 17 },
    { name: "Team", fullName: "Commercial structure, accountability, and KPI alignment", questions: 16 },
    { name: "Pricing", fullName: "Pricing logic, offer design, and revenue architecture", questions: 15 },
    { name: "BD", fullName: "Partnerships, referrals, and growth channels", questions: 18 },
    { name: "Agents", fullName: "Broker and partner network performance management", questions: 28 },
  ];

  const featureAreas = [
    "Strategy",
    "Marketing",
    "Sales",
    "Customer Journey",
    "CRM Systems",
    "Pricing",
    "Partnerships",
    "Team Performance",
  ];

  const plans = [
    {
      name: "Project-Based Advisory",
      description: "Focused support for a defined commercial challenge or initiative.",
      price: "Custom",
      features: [
        "Commercial diagnostic",
        "Strategic recommendations",
        "Targeted advisory support",
        "Defined project scope",
        "Executive guidance",
      ],
      cta: "Contact Us",
      ctaLink: "/contact",
      highlighted: false,
    },
    {
      name: "Commercial Optimisation Programme",
      description: "A structured optimisation model for rebuilding the commercial engine.",
      price: "Custom",
      features: [
        "Commercial diagnostic",
        "Structured plan",
        "Weekly performance calls",
        "Sales & funnel optimisation",
        "CRM and KPI alignment",
        "Leadership collaboration",
      ],
      cta: "Book a Consultation",
      ctaLink: "/contact",
      highlighted: true,
    },
    {
      name: "Monthly Advisory Retainer",
      description: "Ongoing advisory support for continuous execution and performance improvement.",
      price: "Custom",
      features: [
        "Monthly advisory support",
        "Performance review",
        "Ongoing commercial guidance",
        "Funnel and pricing input",
        "Execution support",
      ],
      cta: "Contact Us",
      ctaLink: "/contact",
      highlighted: false,
    },
  ];

  // Chrome metallic icons with halo glow (matches AgencyLandingPage treatment)
  const pillarIcons = [
    // 0 Strategy — crosshair
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <circle cx="12" cy="12" r="9" />
        <circle cx="12" cy="12" r="5.5" />
        <circle cx="12" cy="12" r="2" fill="url(#ralChrome)" />
        <line x1="12" y1="1.5" x2="12" y2="4" />
        <line x1="12" y1="20" x2="12" y2="22.5" />
        <line x1="1.5" y1="12" x2="4" y2="12" />
        <line x1="20" y1="12" x2="22.5" y2="12" />
      </svg>
    ),
    // 1 Marketing — megaphone
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M3 11v2a1 1 0 0 0 1 1h3l8 5V5L7 10H4a1 1 0 0 0-1 1z" />
        <path d="M18.5 8.5a4 4 0 0 1 0 7" />
      </svg>
    ),
    // 2 Sales — funnel
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M3 4h18l-7 9v7l-4-2v-5z" />
      </svg>
    ),
    // 3 Customer Journey / CRM — database
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <ellipse cx="12" cy="5" rx="8" ry="2.5" />
        <path d="M4 5v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5V5" />
        <path d="M4 11v6c0 1.4 3.6 2.5 8 2.5s8-1.1 8-2.5v-6" />
      </svg>
    ),
    // 4 CRM Systems / Team — users
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="3.5" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    // 5 Pricing — tag with euro
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M20.5 11L13 18.5a2 2 0 0 1-2.83 0L3 11.33V2.5h8.83L20.5 11a2 2 0 0 1 0 2.83z" />
        <circle cx="7.5" cy="7" r="1.2" fill="url(#ralChrome)" />
        <path d="M14.2 11.6c-.7-.5-1.6-.8-2.5-.6-1.4.3-2.3 1.6-2 3 .3 1.4 1.6 2.3 3 2 .8-.2 1.4-.7 1.7-1.4M11 13.4h2.6M11 15h2.4" />
      </svg>
    ),
    // 6 Partnerships / BD — share network
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <circle cx="18" cy="5" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <circle cx="6" cy="12" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <circle cx="18" cy="19" r="2.5" fill="url(#ralChrome)" fillOpacity="0.15" />
        <line x1="8.2" y1="10.8" x2="15.8" y2="6.2" />
        <line x1="8.2" y1="13.2" x2="15.8" y2="17.8" />
      </svg>
    ),
    // 7 Team Performance / After-Sales — shield with check
    (
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="url(#ralChrome)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" filter="url(#ralChromeGlow)" aria-hidden="true">
        <path d="M12 2L4 5v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V5z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  ];

  // Lighter variant — chrome stroke, no glow (used inside dense composites like the hero card)
  const pillarIconsLite = pillarIcons.map((node) =>
    React.cloneElement(node, { filter: undefined, strokeWidth: "1.5" })
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <>
      <SEOHead page="home" />

      {/* Shared SVG defs (chrome gradient + soft glow) used by chrome icons */}
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="ralChrome" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#FFFFFF" />
            <stop offset="35%" stopColor="#C4CDD9" />
            <stop offset="65%" stopColor="#9DB4E8" />
            <stop offset="100%" stopColor="#5B7BA8" />
          </linearGradient>
          <filter id="ralChromeGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.2" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
          <linearGradient id="ralChromeBar" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#5B7BA8" />
            <stop offset="50%" stopColor="#9DB4E8" />
            <stop offset="100%" stopColor="#C4CDD9" />
          </linearGradient>
        </defs>
      </svg>

      <div className="min-h-screen bg-[#05070d] text-white relative overflow-hidden">

        {/* background grid */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        {/* glow */}
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-blue-400/10 rounded-full blur-3xl pointer-events-none"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-blue-300/10 rounded-full blur-3xl pointer-events-none"></div>

        {/* NAVBAR */}
        <nav
          className={`fixed top-0 w-full z-50 transition-all ${
            scrolled
              ? "bg-[#05070d]/90 backdrop-blur border-b border-white/10"
              : "bg-transparent"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="RAL" className="w-10" />
              <span className="font-serif text-xl">RAL Real Advisor</span>
            </Link>

            <div className="flex items-center gap-6">
              <Link
                to="/login"
                className="text-sm text-white/80 hover:text-[#9fc6ff] uppercase tracking-wide"
              >
                Sign In
              </Link>

              <Link
                to="/contact"
                className="border border-white/20 px-5 py-3 text-sm uppercase tracking-[0.22em] hover:bg-white/10 transition"
              >
                Contact
              </Link>
            </div>
          </div>
        </nav>

        {/* HERO */}
        <section className="min-h-screen flex items-center pt-24">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20 items-center">

            <Reveal direction="up" duration={1200} distance={60} delay={150}>
              <span className="text-[#9fc6ff] uppercase tracking-[0.3em] text-sm">
                Real Estate Intelligence Platform
              </span>

              <h1 className="text-5xl lg:text-7xl font-serif font-bold leading-tight mt-6 mb-6">
                Understand
                <br />
                <span className="text-[#9fc6ff]">
                  Your Real Estate Business
                </span>
              </h1>

              <p className="text-neutral-400 text-lg max-w-lg mb-10">
                RAL Real Advisor provides a structured diagnostic for real estate
                developers, agencies, and property companies. Analyze your
                strategy, marketing, sales, and operations to uncover growth
                opportunities and performance gaps.
              </p>

              <div className="flex gap-4 flex-wrap">
                <Link
                  to="/register"
                  className="bg-[#9fc6ff] text-[#05070d] px-8 py-4 font-medium uppercase tracking-wide hover:opacity-90 transition"
                >
                  Start Diagnostic
                </Link>
              </div>
            </Reveal>

            {/* HERO CARD — Spider chart */}
            <Reveal
              direction="left"
              duration={1100}
              distance={50}
              delay={400}
              className="hidden lg:block"
            >
              <SpiderChart />
            </Reveal>

          </div>
        </section>

        {/* FEATURES — chrome icons */}
        <section className="py-32">
          <div className="max-w-7xl mx-auto px-6">

            <Reveal direction="up" duration={800}>
              <div className="text-center mb-20">
                <span className="text-[#9fc6ff] uppercase tracking-[0.3em] text-sm">
                  Platform Features
                </span>

                <h2 className="text-4xl font-serif font-bold mt-6">
                  Analyze Every Part of Your Business
                </h2>

                <p className="text-neutral-400 mt-4 max-w-2xl mx-auto">
                  Our diagnostic evaluates key areas of your commercial
                  performance to identify opportunities for growth and
                  operational improvement.
                </p>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {featureAreas.map((area, i) => (
                <Reveal
                  key={i}
                  direction="up"
                  duration={700}
                  distance={30}
                  delay={i * 90}
                >
                  <div className="border border-white/10 p-8 hover:border-[#9fc6ff] transition group h-full relative">
                    <div className="flex items-start justify-between mb-4">
                      <div className="text-4xl font-serif text-[#9fc6ff]">
                        {String(i + 1).padStart(2, "0")}
                      </div>
                      <div
                        className="relative w-10 h-10"
                        style={{
                          filter: "drop-shadow(0 0 6px rgba(159,198,255,0.45))",
                        }}
                      >
                        <div
                          className="absolute inset-0 rounded-full"
                          style={{
                            background:
                              "radial-gradient(circle at 50% 50%, rgba(159,198,255,0.35) 0%, rgba(159,198,255,0.12) 40%, transparent 70%)",
                          }}
                        />
                        <div className="relative w-full h-full">
                          {pillarIcons[i]}
                        </div>
                      </div>
                    </div>

                    <h3 className="text-xl font-serif">{area}</h3>

                    <p className="text-neutral-400 text-sm mt-3">
                      Evaluate performance and identify improvements across this
                      key area of your business.
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>

        {/* PROCESS */}
        <section className="py-32 border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-20">

            <Reveal direction="up" duration={800}>
              <span className="text-[#9fc6ff] uppercase tracking-[0.3em] text-sm">
                Built on Experience
              </span>

              <h2 className="text-4xl font-serif font-bold mt-6 mb-10">
                A Diagnostic Grounded in Real Commercial Practice
              </h2>

              <div className="space-y-8">
                {[
                  {
                    title: "Complete Assessment",
                    desc: "Answer structured questions about your business operations and performance.",
                  },
                  {
                    title: "AI Powered Automated Analysis",
                    desc: "Our system analyzes your responses and benchmarks them against industry standards.",
                  },
                  {
                    title: "Receive Action Plan",
                    desc: "Get a detailed report with recommendations to improve your results.",
                  },
                ].map((step, i) => (
                  <Reveal
                    key={i}
                    direction="left"
                    duration={650}
                    distance={30}
                    delay={200 + i * 120}
                  >
                    <div className="flex gap-6">
                      <div className="w-14 h-14 bg-[#9fc6ff] text-[#05070d] flex items-center justify-center font-bold">
                        {i + 1}
                      </div>

                      <div>
                        <h3 className="font-serif text-xl">{step.title}</h3>
                        <p className="text-neutral-400">{step.desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Reveal>

            <Reveal direction="left" duration={1000} distance={40} delay={300} className="flex items-center justify-center">
              <div className="bg-neutral-800/50 border border-neutral-700 p-12 text-center">
                <div className="text-[#9fc6ff] uppercase text-sm mb-4 tracking-wider">
                  Experience
                </div>

                <svg
                  viewBox="0 0 200 100"
                  className="w-full h-auto"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <text
                    x="100"
                    y="72"
                    textAnchor="middle"
                    fontFamily="Georgia, serif"
                    fontSize="68"
                    fontWeight="700"
                    fill="url(#ralChrome)"
                    filter="url(#ralChromeGlow)"
                  >
                    20+
                  </text>
                </svg>
                <div className="text-neutral-400 mt-2">Years in Real Estate</div>
              </div>
            </Reveal>

          </div>
        </section>

        {/* COMPREHENSIVE ASSESSMENT */}
        <section className="py-24 lg:py-32 bg-white text-neutral-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <Reveal direction="up" duration={800}>
                <span className="text-[#7eaee8] text-sm font-medium tracking-[0.3em] uppercase mb-4 block">
                  Comprehensive Assessment
                </span>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold leading-tight mb-6">
                  100+ Structured Commercial Questions
                </h2>
                <p className="text-lg text-neutral-600 leading-relaxed mb-8">
                  The diagnostic is built around the real commercial challenges
                  faced by developers and growth-focused real estate teams.
                  It gives leadership a practical way to assess strengths,
                  weaknesses, and improvement priorities.
                </p>

                <div className="grid grid-cols-2 gap-4 mb-8">
                  {[
                    { label: "Business Areas", value: "8 Covered" },
                    { label: "Question Set", value: "Well Structured" },
                    { label: "Experience", value: "20+ Years" },
                    { label: "Output", value: "Strategic Report" },
                  ].map((item, i) => (
                    <Reveal
                      key={i}
                      direction="up"
                      duration={600}
                      distance={20}
                      delay={300 + i * 90}
                    >
                      <div className="border border-neutral-200 p-4 bg-neutral-50 h-full">
                        <div className="text-[#7eaee8] font-medium">{item.value}</div>
                        <div className="text-neutral-500 text-sm">{item.label}</div>
                      </div>
                    </Reveal>
                  ))}
                </div>

                <Link
                  to="/contact"
                  className="inline-flex items-center text-[#7eaee8] font-medium hover:text-[#5c97de] transition-colors"
                >
                  Start Diagnostic
                  <svg className="ml-2 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </Reveal>

              <Reveal direction="left" duration={900} distance={40} delay={300}>
                <div className="bg-neutral-100 p-8 border border-neutral-200">
                  <h3 className="text-lg font-medium text-neutral-900 mb-6 tracking-wide uppercase">
                    8 Core Commercial Areas
                  </h3>
                  <div className="space-y-2">
                    {categories.map((cat, index) => (
                      <Reveal
                        key={index}
                        direction="up"
                        duration={500}
                        distance={15}
                        delay={500 + index * 60}
                      >
                        <div
                          className={`cursor-pointer transition-all ${
                            activeAccordion === index ? "bg-[#05070d]" : "bg-white hover:bg-neutral-50"
                          } border border-neutral-200`}
                          onClick={() => setActiveAccordion(index)}
                        >
                          <div className="p-5 flex items-center justify-between">
                            <div className="flex items-center gap-5 flex-1">
                              <span
                                className={`text-3xl font-serif font-bold ${
                                  activeAccordion === index ? "text-[#9fc6ff]" : "text-neutral-300"
                                }`}
                              >
                                0{index + 1}
                              </span>
                              <span
                                className={`text-lg font-medium ${
                                  activeAccordion === index ? "text-white" : "text-neutral-700"
                                }`}
                              >
                                {cat.name}
                              </span>
                            </div>
                            <div className="w-7 h-7 mr-2 shrink-0">
                              {activeAccordion === index ? pillarIcons[index] : pillarIconsLite[index]}
                            </div>
                            <svg
                              className={`w-5 h-5 transition-transform ${
                                activeAccordion === index ? "text-[#9fc6ff] rotate-90" : "text-neutral-300"
                              }`}
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </div>
                          {activeAccordion === index && (
                            <div className="px-5 pb-5">
                              <p className="text-neutral-400 text-sm pl-14">
                                {cat.fullName}
                              </p>
                            </div>
                          )}
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </section>

        {/* INTELLIGENT ANALYSIS */}
        <section className="py-24 lg:py-32 bg-neutral-900 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <Reveal
                direction="right"
                duration={900}
                distance={40}
                delay={150}
                className="order-2 lg:order-1"
              >
                <div className="bg-neutral-800 p-8 border border-neutral-700">
                  <div className="flex items-center gap-4 mb-8">
                    <div className="w-12 h-12 bg-[#7eaee8] flex items-center justify-center">
                      <svg className="w-6 h-6 text-[#05070d]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-white font-medium">Analysis Engine</div>
                      <div className="text-[#9fc6ff] text-sm">Commercial Intelligence</div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-neutral-400">Analyzing commercial data...</span>
                        <span className="text-[#9fc6ff]">Complete</span>
                      </div>
                      <div className="h-1 bg-neutral-700 rounded-full overflow-hidden">
                        <div className="h-full w-full bg-[#7eaee8] rounded-full"></div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-neutral-900 p-4">
                        <div className="text-3xl font-serif font-bold text-white">78</div>
                        <div className="text-neutral-500 text-sm">Overall Score</div>
                      </div>
                      <div className="bg-neutral-900 p-4">
                        <div className="text-3xl font-serif font-bold text-[#9fc6ff]">L4</div>
                        <div className="text-neutral-500 text-sm">Maturity Level</div>
                      </div>
                    </div>

                    <div className="border-t border-neutral-700 pt-6">
                      <div className="text-neutral-400 text-sm mb-4">
                        Priority recommendations:
                      </div>
                      <div className="space-y-2">
                        {[
                          "Improve lead response speed",
                          "Strengthen partner channel performance",
                          "Increase CRM adoption and data discipline",
                        ].map((item, i) => (
                          <div key={i} className="flex items-center gap-2 text-white text-sm">
                            <span className="text-[#9fc6ff]">{i + 1}.</span>
                            {item}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>

              <Reveal
                direction="left"
                duration={900}
                distance={40}
                delay={300}
                className="order-1 lg:order-2"
              >
                <span className="text-[#9fc6ff] text-sm font-medium tracking-[0.3em] uppercase mb-4 block">
                  Intelligent Analysis
                </span>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold leading-tight mb-6">
                  Analysis that turns data into action
                </h2>
                <p className="text-lg text-neutral-400 leading-relaxed mb-8">
                  The platform does more than surface scores. It translates
                  commercial signals into practical recommendations across
                  strategy, marketing, sales, CRM, pricing, and execution.
                </p>

                <ul className="space-y-4">
                  {[
                    "Performance visibility across the commercial engine",
                    "Clear priorities for immediate improvement",
                    "Context-driven recommendations",
                    "A structured basis for planning the next steps",
                  ].map((item, index) => (
                    <Reveal
                      key={index}
                      direction="left"
                      duration={600}
                      distance={25}
                      delay={500 + index * 100}
                    >
                      <li className="flex items-center gap-4">
                        <div className="w-2 h-2 bg-[#9fc6ff]"></div>
                        <span className="text-neutral-300">{item}</span>
                      </li>
                    </Reveal>
                  ))}
                </ul>
              </Reveal>
            </div>
          </div>
        </section>

        {/* PRICING / ENGAGEMENT MODELS */}
        <section className="py-24 bg-neutral-100 text-neutral-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <Reveal direction="up" duration={800}>
              <div className="text-center mb-16">
                <span className="text-[#7eaee8] text-sm font-medium tracking-[0.3em] uppercase mb-4 block">
                  Engagement Models
                </span>
                <h2 className="text-4xl lg:text-5xl font-serif font-bold">
                  Flexible ways to work together
                </h2>
              </div>
            </Reveal>

            <div className="grid lg:grid-cols-3 gap-8">
              {plans.map((plan, index) => (
                <Reveal
                  key={index}
                  direction="up"
                  duration={800}
                  distance={40}
                  delay={200 + index * 150}
                >
                  <div
                    className={`relative border h-full ${
                      plan.highlighted
                        ? "bg-[#05070d] text-white border-white/10"
                        : "bg-white border-neutral-200"
                    }`}
                  >
                    {plan.highlighted && (
                      <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                        <span className="bg-[#7eaee8] text-[#05070d] text-xs font-medium tracking-wider uppercase px-4 py-2">
                          Recommended
                        </span>
                      </div>
                    )}

                    <div className="p-10">
                      <h3
                        className={`text-2xl font-serif font-bold mb-2 ${
                          plan.highlighted ? "text-white" : "text-neutral-900"
                        }`}
                      >
                        {plan.name}
                      </h3>
                      <p
                        className={`text-sm mb-8 ${
                          plan.highlighted ? "text-neutral-400" : "text-neutral-500"
                        }`}
                      >
                        {plan.description}
                      </p>

                      <div className="mb-8">
                        <div
                          className={`text-5xl font-serif font-bold ${
                            plan.highlighted ? "text-white" : "text-neutral-900"
                          }`}
                        >
                          {plan.price}
                        </div>
                        <p
                          className={`text-sm mt-2 ${
                            plan.highlighted ? "text-neutral-500" : "text-neutral-400"
                          }`}
                        >
                          Contact for scope and proposal
                        </p>
                      </div>

                      <Link
                        to={plan.ctaLink}
                        className={`block w-full py-4 text-center font-medium tracking-wide transition-all ${
                          plan.highlighted
                            ? "bg-[#7eaee8] text-[#05070d] hover:opacity-90"
                            : "bg-[#05070d] text-white hover:bg-neutral-900"
                        }`}
                      >
                        {plan.cta}
                      </Link>

                      <div
                        className={`mt-10 pt-10 border-t ${
                          plan.highlighted ? "border-neutral-700" : "border-neutral-200"
                        }`}
                      >
                        <p
                          className={`text-sm font-medium tracking-wide uppercase mb-6 ${
                            plan.highlighted ? "text-neutral-400" : "text-neutral-500"
                          }`}
                        >
                          What&apos;s included
                        </p>
                        <ul className="space-y-4">
                          {plan.features.map((feature, i) => (
                            <li key={i} className="flex items-start gap-3">
                              <svg
                                className={`w-5 h-5 mt-0.5 flex-shrink-0 ${
                                  plan.highlighted ? "text-[#9fc6ff]" : "text-[#7eaee8]"
                                }`}
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span
                                className={
                                  plan.highlighted ? "text-neutral-300" : "text-neutral-600"
                                }
                              >
                                {feature}
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>

            {/* <Reveal direction="up" duration={700} delay={200}>
              <div className="mt-12 text-center">
                <p className="text-neutral-500 text-sm mb-4">Additional formats available</p>
                <div className="flex justify-center items-center gap-8 text-neutral-500 flex-wrap">
                  <span>Leadership Coaching</span>
                  <span>Team Enablement Packages</span>
                </div>
              </div>
            </Reveal> */}
          </div>
        </section>

        {/* CTA */}
        <section className="py-32 border-t border-white/10 text-center">

          <Reveal direction="up" duration={900} distance={30}>
            <span className="text-[#9fc6ff] uppercase tracking-[0.3em] text-sm">
              Start Today
            </span>

            <h2 className="text-4xl lg:text-5xl font-serif font-bold mt-6 mb-6">
              Discover Your Business Potential
            </h2>

            <p className="text-neutral-400 max-w-xl mx-auto mb-10">
              Join companies using RAL Real Advisor to evaluate performance,
              identify opportunities, and improve results.
            </p>
          </Reveal>

          <Reveal direction="up" duration={700} distance={20} delay={300}>
            <Link
              to="/contact"
              className="inline-block bg-[#9fc6ff] text-[#05070d] px-10 py-5 font-medium uppercase tracking-wide hover:opacity-90 transition"
            >
              Start Diagnostic
            </Link>
          </Reveal>

        </section>

        <BackToTop />
      </div>
    </>
  );
};

export default LandingPage;